import { ButtonHTMLAttributes, ReactNode, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { Tooltip } from '../../components/Tooltip';
import { datasetToAttributes } from '../../utils';
import { Icon, IconProps } from '../Icon';
import type { ButtonIconPosition, ButtonShape, ButtonSize, ButtonVariant } from './Button.style';
import { CustomButtonProps, StyledButton, StyledButtonLoader, StyledIcon } from './Button.style';

export interface ButtonProps {
  opacity?: number;
  children?: ReactNode;
  color?: string;
  active?: boolean;
  disabled?: boolean;
  icon?: {
    name: string;
    color?: string;
    size?: IconProps['size'];
    angle?: number;
    position?: ButtonIconPosition;
  };
  label?: string;
  action?: string;
  loading?: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  shape?: ButtonShape;
  size?: ButtonSize;
  title?: string | ReactNode;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  variant?: ButtonVariant;
  textColor?: string;
  textColorHover?: string;
  dataset?: Record<string, string>;
  custom?: CustomButtonProps;
  danger?: boolean;
}

export function Button({
  children,
  color,
  active = false,
  disabled = false,
  icon,
  label,
  loading = false,
  onClick,
  opacity,
  shape = 'default',
  size = 'Medium',
  title,
  type = 'button',
  variant = 'primary',
  dataset,
  action,
  textColor,
  textColorHover,
  custom,
  danger = false,
}: ButtonProps) {
  const dataAttributes = useMemo(() => {
    const actionName = action ?? label;
    const attributes = actionName
      ? {
          ...dataset,
          action: actionName,
        }
      : dataset;

    return datasetToAttributes({ ...attributes });
  }, [dataset, action, label]);

  return (
    <Tooltip text={title} visible={!!title}>
      <StyledButton
        {...dataAttributes}
        $active={active}
        $variant={variant}
        $size={size}
        disabled={disabled}
        $iconPosition={icon?.position ?? 'start'}
        onClick={disabled ? undefined : onClick}
        $shape={shape}
        type={type}
        $color={color}
        $opacity={opacity}
        $textColor={textColor}
        $textColorHover={textColorHover}
        $custom={custom}
        $danger={danger}
      >
        {icon && !loading && (
          <StyledIcon>
            <Icon
              {...(icon.color ? { color: icon.color } : {})}
              name={icon.name}
              size={icon.size ?? 'Medium'}
              angle={icon.angle}
            />
          </StyledIcon>
        )}
        {loading && (
          <StyledIcon>
            <StyledButtonLoader />
          </StyledIcon>
        )}
        {children || label}
      </StyledButton>
    </Tooltip>
  );
}
